<div class="mb-8 max-w-2xl mx-auto px-4 sm:px-6 mt-6">

  @let organization = (organization$ | async);
  @let options = (options$ | async) || [];

  @if (organization === undefined) {
  <div class="p-6 rounded-2xl border border-blueGray-200 bg-white text-center text-xs text-blueGray-500">
    <i class="fas fa-circle-notch fa-spin mr-1.5"></i>Loading…
  </div>
  } @else if (!organization) {
  <div class="p-6 rounded-2xl border border-dashed border-blueGray-200 bg-white text-center space-y-2">
    <i class="fas fa-sitemap text-blueGray-300 text-2xl"></i>
    <h1 class="text-sm font-bold text-blueGray-800">Organization not found</h1>
    <p class="text-xs text-blueGray-500">This join link may be out of date. Ask the organization for a new one.</p>
  </div>
  } @else {

  <!-- Organization -->
  <div class="bg-white rounded-2xl border border-blueGray-200 shadow-2xs p-5 flex items-center gap-4">
    @if (organization.logoUrl) {
    <img [src]="organization.logoUrl" [alt]="organization.name"
      class="h-14 w-14 rounded-xl object-cover border border-blueGray-200 shrink-0" />
    } @else {
    <div
      class="h-14 w-14 rounded-xl bg-primary-100 text-primary-700 font-bold text-lg flex items-center justify-center shrink-0">
      {{ organization.name | slice:0:2 | uppercase }}
    </div>
    }
    <div class="min-w-0">
      <span class="text-[10px] uppercase font-bold text-blueGray-400 tracking-wider block">You're invited to join</span>
      <h1 class="text-lg font-bold text-blueGray-900 truncate">{{ organization.name }}</h1>
      @if (organization.description) {
      <p class="text-xs text-blueGray-500 mt-0.5">{{ organization.description }}</p>
      }
    </div>
  </div>

  <!-- Churches the user administers -->
  <div class="mt-6 space-y-3">
    <div>
      <h2 class="text-sm font-bold text-blueGray-800">Choose your church</h2>
      <p class="text-[11px] text-blueGray-500 mt-0.5">
        Send a request for the church you administer. {{ organization.name }} reviews it before your church joins.
        A church can belong to one organization at a time.
      </p>
    </div>

    @for (option of options; track option.church.id) {
    <div class="bg-white rounded-xl border border-blueGray-200 p-4 space-y-3">
      <div class="flex items-center justify-between gap-3">
        <div class="flex items-center gap-3 min-w-0">
          @if (option.church.imageUrl) {
          <img [src]="option.church.imageUrl" [alt]="option.church.name"
            class="h-10 w-10 rounded-lg object-cover border border-blueGray-200 shrink-0" />
          } @else {
          <div
            class="h-10 w-10 rounded-lg bg-blueGray-100 text-blueGray-600 font-bold text-xs flex items-center justify-center shrink-0">
            {{ option.church.name | slice:0:2 | uppercase }}
          </div>
          }
          <span class="text-sm font-semibold text-blueGray-800 truncate">{{ option.church.name }}</span>
        </div>

        @switch (option.status) {
        @case ('member') {
        <span class="px-2 py-1 rounded-lg text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100 shrink-0">
          <i class="fas fa-check mr-1"></i>Already a member
        </span>
        }
        @case ('pending') {
        <span class="px-2 py-1 rounded-lg text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-100 shrink-0">
          <i class="fas fa-hourglass-half mr-1"></i>Request pending
        </span>
        }
        @case ('otherOrg') {
        <span class="px-2 py-1 rounded-lg text-[10px] font-bold bg-blueGray-100 text-blueGray-600 shrink-0">
          In another organization
        </span>
        }
        @default {
        @if (openChurchId !== option.church.id) {
        <button type="button" (click)="openRequest(option.church)"
          class="px-3 py-1.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-xs font-semibold shadow-2xs transition-colors cursor-pointer shrink-0">
          Request to join
        </button>
        }
        }
        }
      </div>

      @if (option.status === 'otherOrg') {
      <p class="text-[11px] text-blueGray-500">
        Leave its current organization first, from the church's
        <a [routerLink]="['/groups', option.church.id, 'affiliation']" class="text-primary-600 font-semibold hover:underline">
          Governance &amp; Affiliation</a> page.
      </p>
      }

      @if (openChurchId === option.church.id) {
      <div class="space-y-2">
        <textarea [(ngModel)]="joinMessage" rows="2" maxlength="500"
          placeholder="Optional message to the organization"
          class="w-full px-3 py-2 text-xs rounded-xl border border-blueGray-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"></textarea>
        <div class="flex justify-end gap-2">
          <button type="button" (click)="cancelRequest()" [disabled]="submittingChurchId === option.church.id"
            class="px-3 py-1.5 rounded-xl text-xs font-semibold text-blueGray-600 hover:bg-blueGray-100 cursor-pointer">
            Cancel
          </button>
          <button type="button" (click)="sendRequest(option.church, organization)"
            [disabled]="submittingChurchId === option.church.id"
            class="px-3 py-1.5 rounded-xl bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-xs font-semibold shadow-2xs cursor-pointer">
            {{ submittingChurchId === option.church.id ? 'Sending…' : 'Send request' }}
          </button>
        </div>
      </div>
      }
    </div>
    } @empty {
    <div class="p-5 rounded-xl border border-dashed border-blueGray-200 bg-blueGray-50/50 text-xs text-blueGray-600 space-y-1">
      <p class="font-semibold text-blueGray-800">You don't administer any church yet.</p>
      <p>
        Only a church's admins can ask to join an organization. Share this link with your church's admin,
        or create your church first from
        <a routerLink="/groups/create" class="text-primary-600 font-semibold hover:underline">Create group</a>.
      </p>
    </div>
    }
  </div>
  }
</div>
